import React from 'react';
import { RootState, store } from 'src/app/store';
import {
  selectUserInfoFromHeader,
  updateHasSalesAssistantLoaded,
  UserInfo,
} from '../../store/slice';
import { connect } from 'react-redux';
import { canShowSalesAssistant, createMountProps } from './salesAssistantUtils';
import FederatedMount from './FederatedMount';
import FederatedRemote, {
  FederatedLoader,
  WrappedFederatedLoader,
} from './FederatedRemote';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';

/*
 * NOTE: This is to mitigate an issue where our package versions
 * conflict with Bell drawer. By delaying the load, we allow Bell drawer
 * to load first, select its react version, then we load.
 *
 * In this case, we just need Bell drawer's version of this import to be invoked
 * first
 *
 * Ideally, all parties involved need to have a correct module federation
 * configuration and handle package versioning correctly
 */
const assistantImportFn = () =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  import('salesAssistant/ChatBotComponent');

export const assistantLoader: WrappedFederatedLoader = (
  importFunction: FederatedLoader,
): FederatedLoader => {
  return () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, 2000);
    }).then(() => {
      return importFunction();
    });
  };
};

interface SalesAssistantModuleProps {
  userInfo: UserInfo;
}

// The dynamically imported sales-assistant module exposes a `mount` entry
// point; `mount` is the only member this component consumes from it.
interface LoadedSalesAssistantModule {
  mount: (element: HTMLElement | null, props: unknown) => void;
}

const SalesAssistantModule = (props: SalesAssistantModuleProps) => {
  const userEmail = props.userInfo?.customerInfo?.primaryEmail || '';
  const userName = props.userInfo?.customerInfo?.fullName || '';
  const chromeConfigContext = useInternalChromeConfigContext();
  const canShow = canShowSalesAssistant(
    userEmail,
    chromeConfigContext.config?.isInternalView,
  );

  if (!canShow) return null;

  const amazonAlias = userEmail.split('@')[0] ?? '';
  const remoteProps = {
    loader: assistantLoader(assistantImportFn),
    timeout: 12000,
    afterLoad: () => {
      store.dispatch(updateHasSalesAssistantLoaded(true));
    },
    render: (loaded: LoadedSalesAssistantModule | undefined) => {
      if (!loaded) return null;
      const { mount } = loaded;

      return (
        <FederatedMount
          containerId="sales-assistant-drawer"
          mount={mount}
          mountProps={createMountProps(
            amazonAlias,
            userName,
            chromeConfigContext,
          )}
        />
      );
    },
  };

  return <FederatedRemote {...remoteProps} />;
};

const mapStateToProps = (state: RootState) => {
  return {
    userInfo: selectUserInfoFromHeader(state),
  };
};

export default connect(mapStateToProps)(SalesAssistantModule);
